import React, { useState, useMemo } from 'react';
import ProductGrid from './ProductGrid';
import Pagination from './Pagination';
import './Products.css';

const Products = () => {
  const [activeTab, setActiveTab] = useState('tab-1');
  const [exidePage, setExidePage] = useState(1);
  const [luminousPage, setLuminousPage] = useState(1);
  const [amaronPage, setAmaronPage] = useState(1);
  const [lithiumPage, setLithiumPage] = useState(1);
  const [solarPage, setSolarPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  const exideCategories = [
    { id: 'motor-cycle', name: 'EXIDE XPLORE - Motor Cycle Batteries', products: [], brand: 'exide' },
    { id: 'car', name: 'Exide Car Battery', products: [], brand: 'exide' },
    { id: 'heavy-vehicles', name: 'Exide Heavy Vehicles Battery', products: [], brand: 'exide' },
    { id: 'invamaster', name: 'EXIDE INVAMASTER', products: [], brand: 'exide' },
    { id: 'invatubular', name: 'EXIDE INVATUBULAR', products: [], brand: 'exide' },
    { id: 'instabrite', name: 'EXIDE EL', products: [], brand: 'exide' },
    { id: 'solar', name: 'EXIDE TUBULAR SOLAR BATTERY', products: [], brand: 'exide' }
  ];

  const luminousCategories = [
    { id: 'inverlast', name: 'INVERLAST - Luminous Batteries', products: [], brand: 'luminous' },
    { id: 'extra-charge', name: 'EXTRA CHARGE', products: [], brand: 'luminous' },
    { id: 'red-charge', name: 'RED CHARGE', products: [], brand: 'luminous' },
    { id: 'power-charge', name: 'POWER CHARGE', products: [], brand: 'luminous' },
    { id: 'shakti-charge', name: 'SHAKTI CHARGE', products: [], brand: 'luminous' },
    { id: 'invergel', name: 'INVERGEL', products: [], brand: 'luminous' },
    { id: 'life-max', name: 'LIFE MAX', products: [], brand: 'luminous' },
    { id: 'solar-battery', name: 'Luminous Solar Battery', products: [], brand: 'luminous' }
  ];

  const amaronCategories = [
    { id: 'car-batteries', name: 'Car Batteries', products: [], brand: 'amaron' },
    { id: 'pro-rider', name: 'Pro Rider Batteries', products: [], brand: 'amaron' },
    { id: 'inverter-batteries', name: 'Inverter Batteries', products: [], brand: 'amaron' },
    { id: 'tubular-batteries', name: 'Tubular Batteries', products: [], brand: 'amaron' },
    { id: 'solar-combos', name: 'Solar Combos', products: [], brand: 'amaron' }
  ];

  const lithiumCategories = [
    { id: 'torque-lithium', name: 'TORQUE 12.8V Lithium Batteries', products: [], brand: 'lithium' },
    { id: 'wall-mounted', name: 'Smart Wall-Mounted Inverter', products: [], brand: 'lithium' },
    { id: 'solar-solutions', name: 'Lithium Solar Solutions', products: [], brand: 'lithium' }
  ];

  const solarCategories = [
    { id: 'off-grid', name: 'Solar Off-Grid Systems', products: [], brand: 'solar' },
    { id: 'on-grid', name: 'Solar On-Grid Systems', products: [], brand: 'solar' },
    { id: 'hybrid', name: 'Solar Hybrid Systems', products: [], brand: 'solar' }
  ];

  // All categories combined for search
  const allCategories = [
    ...exideCategories,
    ...luminousCategories,
    ...amaronCategories,
    ...lithiumCategories,
    ...solarCategories
  ];

  // Search keyword mapping for intelligent search
  const searchKeywordMap = {
    'car': ['car', 'vehicle', 'automotive', 'car-batteries'],
    'car battery': ['car', 'vehicle', 'automotive', 'car-batteries'],
    'car batteries': ['car', 'vehicle', 'automotive', 'car-batteries'],
    'bike': ['motor-cycle', 'bike', 'motorcycle', 'scooter', 'two-wheeler'],
    'bike battery': ['motor-cycle', 'bike', 'motorcycle', 'scooter', 'two-wheeler'],
    'motor cycle': ['motor-cycle', 'motorcycle', 'bike', 'scooter'],
    'motor cycle battery': ['motor-cycle', 'motorcycle', 'bike', 'scooter'],
    'heavy': ['heavy-vehicles', 'heavy', 'truck', 'bus', 'commercial'],
    'heavy vehicles': ['heavy-vehicles', 'heavy', 'truck', 'bus', 'commercial'],
    'invamaster': ['invamaster', 'inverter', 'ups'],
    'inverter': ['invamaster', 'invatubular', 'inverter-batteries', 'solar-inverters', 'inverter', 'ups'],
    'inverter battery': ['invamaster', 'invatubular', 'inverter-batteries', 'inverter', 'ups'],
    'invatubular': ['invatubular', 'tubular', 'tall tubular'],
    'lithium': ['torque-lithium', 'wall-mounted', 'solar-solutions', 'lithium'],
    'torque': ['torque-lithium', 'lithium'],
    'wall-mounted': ['wall-mounted', 'inverter', 'lithium'],
    'solar': ['solar', 'solar-battery', 'solar-combos', 'off-grid', 'on-grid', 'hybrid', 'photovoltaic', 'solar-solutions'],
    'solar battery': ['solar', 'solar-battery', 'solar-combos', 'off-grid', 'on-grid', 'hybrid', 'photovoltaic', 'solar-solutions'],
    'solar panel': ['off-grid', 'on-grid', 'hybrid'],
    'solar inverter': ['off-grid', 'on-grid', 'hybrid', 'wall-mounted'],
    'off-grid': ['off-grid'],
    'on-grid': ['on-grid'],
    'hybrid': ['hybrid']
  };

  // Find matching categories based on search query
  const findMatchingCategories = (query) => {
    const searchLower = query.toLowerCase().trim();

    if (!searchLower) return [];

    // Find matching keywords
    let matchedKeywords = [];

    // First, check for exact phrase matches
    for (const [key, values] of Object.entries(searchKeywordMap)) {
      const keyLower = key.toLowerCase();

      // Check if search contains the key (for "car battery" in "car battery search")
      if (searchLower.includes(keyLower)) {
        matchedKeywords = [...matchedKeywords, ...values];
      }

      // Also check if key contains search (for "car" in "car battery")
      if (keyLower.includes(searchLower)) {
        matchedKeywords = [...matchedKeywords, ...values];
      }
    }

    // If no keyword match, use the query itself
    if (matchedKeywords.length === 0) {
      matchedKeywords = [searchLower];
    }

    // Remove duplicates
    matchedKeywords = [...new Set(matchedKeywords)];

    // Find categories that match any keyword
    const matchingCategories = allCategories.filter(category => {
      const categoryLower = category.name.toLowerCase();
      const categoryIdLower = category.id.toLowerCase();

      // Check if category name or ID matches any keyword
      return matchedKeywords.some(keyword =>
        categoryLower.includes(keyword) ||
        categoryIdLower.includes(keyword) ||
        keyword.includes(categoryIdLower)
      );
    });

    return matchingCategories;
  };

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (value.trim()) {
      const matches = findMatchingCategories(value);
      setSearchResults(matches);
    } else {
      setSearchResults(null);
    }

    // Reset pages
    setExidePage(1);
    setLuminousPage(1);
    setAmaronPage(1);
    setLithiumPage(1);
    setSolarPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (searchQuery.trim()) {
        const matches = findMatchingCategories(searchQuery);
        setSearchResults(matches);
      }
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  // Group search results by brand
  const groupedResults = useMemo(() => {
    if (!searchResults) return null;

    const grouped = {
      exide: searchResults.filter(cat => cat.brand === 'exide'),
      luminous: searchResults.filter(cat => cat.brand === 'luminous'),
      amaron: searchResults.filter(cat => cat.brand === 'amaron'),
      lithium: searchResults.filter(cat => cat.brand === 'lithium'),
      solar: searchResults.filter(cat => cat.brand === 'solar')
    };

    return grouped;
  }, [searchResults]);

  // Check if we should show search results
  const showSearchResults = searchQuery.trim() && searchResults && searchResults.length > 0;

  // Custom ProductGrid for search results
  const SearchResultsGrid = ({ categories, brand }) => {
    return (
      <div>
        {categories.map((category) => (
          <div key={`${brand}-${category.id}`} className="category-section mb-5" data-category={category.id}>
            <h5 className="mt-4 mb-3 fw-bold">{category.name}</h5>

            <div className="row g-4">
              {(() => {
                const imagePaths = {
                  exide: {
                    'motor-cycle': ['/assets/Exide/EXIDE XPLORE 12XL2.5L-C 2.5 Ah-Battery.jpg', '/assets/Exide/EXIDE XPLORE XLTZ4A  4 Ah Battery.jpg', '/assets/Exide/z54.jpg', '/assets/Exide/z65.jpg', '/assets/Exide/EXIDE XPLORE 12XL5L-B 5 Ah Battery.jpg', '/assets/Exide/EXIDE XPLORE XLTZ7 6 Ah Battery.jpg', '/assets/Exide/xltz9.jpg', '/assets/Exide/EXIDE-XPLORE-12XL9-B-9-Ah-Battery.jpg', '/assets/Exide/EXIDE-XPLORE--XLTX14-12-Ah-Battery.jpg'],
                    'car': ['/assets/Exide/EXIDE-MILEAGE-ML38B20L-R-Battery.jpg', '/assets/Exide/EXIDE MILEAGE ML40LBH-RBH Battery.jpg', '/assets/Exide/EXIDE-MILEAGE-MLDIN44R-LH-Battery.jpg', '/assets/Exide/EXIDE-MILEAGE-ML45D21LBH-Battery.jpg', '/assets/Exide/EXIDE-MILEAGE-MLDIN50-Battery.jpg', '/assets/Exide/EXIDE-MILEAGE-ML55D23L-Battery.jpg', '/assets/Exide/EXIDE MILEAGE MLDIN55-R Battery.jpg', '/assets/Exide/EXIDE MILEAGE MLDIN60 Battery.jpg', '/assets/Exide/EXIDE MILEAGE MLDIN65LH Battery.jpg', '/assets/Exide/EXIDE MILEAGE MLDIN66 Battery.jpg', '/assets/Exide/EXIDE MILEAGE ML75D23LBH  Battery.jpg', '/assets/Exide/EXIDE-MILEAGE-MLDIN80-Batte.jpg', '/assets/Exide/EXIDE MILEAGEMLM42(ISS)Battery.jpg', '/assets/Exide/EXIDE MILEAGE ML-DIN47RMF-ISS Battery.jpg', '/assets/Exide/EXIDE MILEAGE MLDIN70-ISS-Battery.jpg', '/assets/Exide/EXIDE-EEZY-EY34B19L-Battery.jpg', '/assets/Exide/EXIDE EEZY EY700L-700  Battery.jpg', '/assets/Exide/EXIDE-EEZY-EY80D26R-Battery.jpg', '/assets/Exide/EXIDE-EEZY-EY105D31L-R-Batt.jpg'],
                  },
                  luminous: {
                    'inverlast': ['/assets/Luminous/INVERLAST - ILST 8036.png', '/assets/Luminous/INVERLAST - ILST 10036.png', '/assets/Luminous/INVERLAST - ILST 12042.png', '/assets/Luminous/INVERLAST - ILTJ 18148.png', '/assets/Luminous/INVERLAST - ILTT 18000N.png', '/assets/Luminous/INVERLAST - ILTT 20048.png', '/assets/Luminous/INVERLAST - ILTT 24060.png', '/assets/Luminous/INVERLAST - ILTT 25060.png', '/assets/Luminous/INVERLAST - ILTT 26060.png', '/assets/Luminous/INVERLAST - XL 18060.png', '/assets/Luminous/INVERLAST - XL 18066.png', '/assets/Luminous/INVERLAST - XL 26060.jpg', '/assets/Luminous/INVERLAST - ILTT 18048N.png'],
                  },
                  amaron: {
                    'car-batteries': ['/assets/Amaron/Amaron-GO-36B20L.jpg', '/assets/Amaron/Amaron-GO-38B20L.jpg', '/assets/Amaron/Amaron-GO-35-BH40B20L.jpg', '/assets/Amaron/Amaron-GO-35-BH38B20R.jpg', '/assets/Amaron/Amaron-GO-42B20R.jpg', '/assets/Amaron/Amaron-GO-50B20L.jpg', '/assets/Amaron/Amaron-GO-80-D-23L.jpg'],
                  },
                  lithium: {
                    'torque-lithium': ['/assets/Lithium/L2.jpg', '/assets/Lithium/L.jpg', '/assets/Lithium/Lithium.jpg'],
                    'wall-mounted': ['/assets/Lithium/L2.jpg', '/assets/Lithium/L.jpg'],
                    'solar-solutions': ['/assets/Lithium/Lithium.jpg']
                  },
                  solar: {
                    'off-grid': ['/assets/Solar/SOLAR-1KVA.jpeg', '/assets/Solar/SOLAR-2KVA.jpeg', '/assets/Solar/SOLAR-3KVA.jpeg', '/assets/Solar/SOLAR-4KVA.jpeg', '/assets/Solar/SOLAR-8KVA.jpeg', '/assets/Solar/SOLAR-10KVA.jpeg'],
                  }
                };

                const productNames = {
                  exide: {
                    'motor-cycle': ['EXIDE XPLORE 12XL2.5L-C 2.5 Ah Battery', 'EXIDE XPLORE XLTZ4A 4 Ah Battery', 'EXIDE XPLORE XLTZ5 4 Ah Battery', 'EXIDE XPLORE XLTZ6 5 Ah Battery', 'EXIDE XPLORE 12XL5L-B 5 Ah Battery', 'EXIDE XPLORE XLTZ7 6 Ah Battery', 'EXIDE XPLORE XLTZ9 8 Ah Battery', 'EXIDE XPLORE 12XL9-B 9 Ah Battery', 'EXIDE XPLORE XLTX14 12 Ah Battery'],
                    'car': ['EXIDE MILEAGE ML38B20L-R Battery', 'EXIDE MILEAGE ML40LBH-RBH Battery', 'EXIDE MILEAGE MLDIN44R-LH Battery', 'EXIDE MILEAGE ML45D21LBH Battery', 'EXIDE MILEAGE MLDIN50 Battery', 'EXIDE MILEAGE ML55D23L Battery', 'EXIDE MILEAGE MLDIN55-R Battery', 'EXIDE MILEAGE MLDIN60 Battery', 'EXIDE MILEAGE MLDIN65LH Battery', 'EXIDE MILEAGE MLDIN66 Battery', 'EXIDE MILEAGE ML75D23LBH Battery', 'EXIDE MILEAGE MLDIN80 Battery', 'EXIDE MILEAGE MLM42 ISS Battery', 'EXIDE MILEAGE ML-DIN47RMF-ISS Battery', 'EXIDE MILEAGE MLDIN70 ISS Battery', 'EXIDE EEZY EY34B19L Battery', 'EXIDE EEZY EY700L -700 Battery', 'EXIDE EEZY EY80D26R Battery', 'EXIDE EEZY EY105D31L-R Battery'],
                  },
                  luminous: {
                    'inverlast': ['INVERLAST - ILST 8036', 'INVERLAST - ILST 10036', 'INVERLAST - ILST 12042', 'INVERLAST - ILTJ 18148', 'INVERLAST - ILTT 18000N', 'INVERLAST - ILTT 20048', 'INVERLAST - ILTT 24060', 'INVERLAST - ILTT 25060', 'INVERLAST - ILTT 26060', 'INVERLAST - XL 18060', 'INVERLAST - XL 18066', 'INVERLAST - XL 26060', 'INVERLAST - ILTT 18048N'],
                  },
                  amaron: {
                    'car-batteries': ['Amaron GO 36B20L', 'Amaron GO 38B20L', 'Amaron GO 35 BH40B20L', 'Amaron GO 35 BH38B20R', 'Amaron GO 42B20R', 'Amaron GO 50B20L', 'Amaron GO 80 D 23L'],
                  },
                  lithium: {
                    'torque-lithium': ['TORQUE 12.8V 1280Wh Lithium Battery', 'TORQUE 12.8V 200Ah LiFePO4 Battery', 'TORQUE Lithium Advanced BMS System'],
                    'wall-mounted': ['TORQUE Smart Wall-Mounted Inverter 5KVA', 'TORQUE Wall-Mount Hybrid Inverter 3KVA'],
                    'solar-solutions': ['TORQUE Lithium Solar Complete System']
                  },
                  solar: {
                    'off-grid': ['Solar Inverter 1KVA Pure Sine Wave', 'Solar Inverter 2KVA Pure Sine Wave', 'Solar Inverter 3KVA Pure Sine Wave', 'Solar Inverter 4KVA MPPT', 'Solar Inverter 8KVA Pure Sine Wave', 'Solar Inverter 10KVA MPPT'],
                  }
                };

                const images = imagePaths[brand]?.[category.id] || [];
                const names = productNames[brand]?.[category.id] || [];

                if (images.length === 0) {
                  return <div className="alert alert-warning">No products found in this category</div>;
                }

                return images.map((image, index) => (
                  <div key={`${brand}-${category.id}-${index}`} className="col-md-6 col-lg-4 col-xl-3">
                    <div className="product-card" style={{ display: 'flex', flexDirection: 'column', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', backgroundColor: '#fff', transition: 'transform 0.3s, box-shadow 0.3s', animation: `fadeInUp 0.6s ease-out forwards`, animationDelay: `${(index % 4) * 0.2}s`, opacity: 1, cursor: 'pointer', height: '100%' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}>
                      <div style={{ position: 'relative', width: '100%', height: '200px', backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                        <img src={image} alt={names[index] || category.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '10px', display: 'block' }} />
                      </div>
                      <div style={{ padding: '16px', textAlign: 'center', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ display: 'block', fontSize: '16px', fontWeight: '600', color: '#333', textDecoration: 'none', lineHeight: '1.4' }}>{names[index] || `${category.name} Product ${index + 1}`}</span>
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="container-fluid product py-5" id="products">
      <div className="container py-5">
        <div className="tab-class">
          <div className="row g-4 align-items-center mb-5">
            <div className="col-lg-2 col-md-12 text-start wow fadeInLeft" data-wow-delay="0.1s">
              <h1 className="mb-0" style={{ fontSize: '30px' }}>
                {showSearchResults ? 'Search Results' : 'Our Products'}
              </h1>
            </div>

            <div className="col-lg-3 col-md-8">
              <div className="position-relative">
                <div className="d-flex border rounded-pill" style={{ backgroundColor: '#fff', borderColor: '#ddd' }}>
                  <input
                    className="form-control border-0 rounded-pill w-100 py-2"
                    type="text"
                    placeholder="Search: car, bike, solar, inverter..."
                    value={searchQuery}
                    onChange={handleSearch}
                    onKeyPress={handleKeyPress}
                    style={{ fontSize: '14px' }}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="btn btn-link text-danger"
                      onClick={clearSearch}
                      style={{ border: '0', fontSize: '14px' }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
                {searchQuery && !showSearchResults && (
                  <div className="mt-2 p-2" style={{ backgroundColor: '#fff3cd', borderRadius: '8px', fontSize: '12px', color: '#856404', border: '1px solid #ffeaa7' }}>
                    <small>
                      <i className="fas fa-info-circle me-1"></i>
                      Try: <strong>car</strong>, <strong>bike</strong>, <strong>solar</strong>, <strong>lithium</strong>, <strong>inverter</strong>
                    </small>
                  </div>
                )}
              </div>
            </div>

            {/* Show brand tabs only when NOT searching */}
            {!showSearchResults && (
              <div className="col-lg-7 col-md-4 text-end wow fadeInRight" data-wow-delay="0.1s">
                <ul className="nav nav-pills d-inline-flex text-center mb-0 flex-wrap justify-content-end gap-2">
                  <li className="nav-item">
                    <button className={`d-flex py-2 px-3 bg-light rounded-pill ${activeTab === 'tab-1' ? 'active' : ''}`} onClick={() => setActiveTab('tab-1')} style={{ border: 'none', fontSize: '14px' }}>
                      <span className="text-dark">Exide</span>
                    </button>
                  </li>
                  <li className="nav-item">
                    <button className={`d-flex py-2 px-3 bg-light rounded-pill ${activeTab === 'tab-2' ? 'active' : ''}`} onClick={() => setActiveTab('tab-2')} style={{ border: 'none', fontSize: '14px' }}>
                      <span className="text-dark">Luminous</span>
                    </button>
                  </li>
                  <li className="nav-item">
                    <button className={`d-flex py-2 px-3 bg-light rounded-pill ${activeTab === 'tab-3' ? 'active' : ''}`} onClick={() => setActiveTab('tab-3')} style={{ border: 'none', fontSize: '14px' }}>
                      <span className="text-dark">Amaron</span>
                    </button>
                  </li>
                  <li className="nav-item">
                    <button className={`d-flex py-2 px-3 bg-light rounded-pill ${activeTab === 'tab-5' ? 'active' : ''}`} onClick={() => setActiveTab('tab-5')} style={{ border: 'none', fontSize: '14px' }}>
                      <span className="text-dark">Lithium</span>
                    </button>
                  </li>
                  <li className="nav-item">
                    <button className={`d-flex py-2 px-3 bg-light rounded-pill ${activeTab === 'tab-4' ? 'active' : ''}`} onClick={() => setActiveTab('tab-4')} style={{ border: 'none', fontSize: '14px' }}>
                      <span className="text-dark">Solar</span>
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>

          <div className="tab-content">
            {/* SEARCH RESULTS VIEW */}
            {showSearchResults && (
              <div className="search-results-view">
                <div className="mb-4 text-center">
                  <h3 className="mb-3">Search Results for "{searchQuery}"</h3>
                  <p className="text-muted">Showing results across all brands</p>
                </div>

                {groupedResults.exide.length > 0 && (
                  <div className="brand-results mb-5">
                    <div className="brand-header mb-4">
                      <h4 className="fw-bold" style={{ color: '#DE400E' }}>
                        <i className="fas fa-bolt me-2"></i> Exide Batteries
                      </h4>
                    </div>
                    <SearchResultsGrid categories={groupedResults.exide} brand="exide" />
                  </div>
                )}

                {groupedResults.luminous.length > 0 && (
                  <div className="brand-results mb-5">
                    <div className="brand-header mb-4">
                      <h4 className="fw-bold" style={{ color: '#2E8B57' }}>
                        <i className="fas fa-sun me-2"></i> Luminous Batteries
                      </h4>
                    </div>
                    <SearchResultsGrid categories={groupedResults.luminous} brand="luminous" />
                  </div>
                )}

                {groupedResults.amaron.length > 0 && (
                  <div className="brand-results mb-5">
                    <div className="brand-header mb-4">
                      <h4 className="fw-bold" style={{ color: '#0066CC' }}>
                        <i className="fas fa-car-battery me-2"></i> Amaron Batteries
                      </h4>
                    </div>
                    <SearchResultsGrid categories={groupedResults.amaron} brand="amaron" />
                  </div>
                )}

                {groupedResults.lithium.length > 0 && (
                  <div className="brand-results mb-5">
                    <div className="brand-header mb-4">
                      <h4 className="fw-bold" style={{ color: '#DC143C' }}>
                        <i className="fas fa-battery-full me-2"></i> TORQUE Lithium Batteries
                      </h4>
                    </div>
                    <SearchResultsGrid categories={groupedResults.lithium} brand="lithium" />
                  </div>
                )}

                {groupedResults.solar.length > 0 && (
                  <div className="brand-results mb-5">
                    <div className="brand-header mb-4">
                      <h4 className="fw-bold" style={{ color: '#FF9900' }}>
                        <i className="fas fa-solar-panel me-2"></i> Solar Products
                      </h4>
                    </div>
                    <SearchResultsGrid categories={groupedResults.solar} brand="solar" />
                  </div>
                )}
              </div>
            )}

            {/* NO SEARCH RESULTS */}
            {searchQuery.trim() && searchResults && searchResults.length === 0 && (
              <div className="alert alert-warning text-center py-5">
                <i className="fas fa-search fa-3x mb-3 text-muted"></i>
                <h4>No results found for "{searchQuery}"</h4>
                <p className="mb-3">Try these search terms:</p>
                <div className="d-flex flex-wrap justify-content-center gap-2">
                  <span className="badge bg-light text-dark p-2">car battery</span>
                  <span className="badge bg-light text-dark p-2">bike battery</span>
                  <span className="badge bg-light text-dark p-2">solar battery</span>
                  <span className="badge bg-light text-dark p-2">lithium battery</span>
                  <span className="badge bg-light text-dark p-2">inverter</span>
                </div>
              </div>
            )}

            {/* NORMAL TABS - Only show when NOT searching */}
            {!showSearchResults && (
              <>
                <div id="tab-1" className={`tab-pane fade show p-0 ${activeTab === 'tab-1' ? 'active' : ''}`}>
                  <ProductGrid categories={exideCategories} currentPage={exidePage} brand="exide" searchQuery="" />
                  <Pagination totalPages={exideCategories.length} currentPage={exidePage} onPageChange={setExidePage} paginationId="exide-pagination" />
                </div>

                <div id="tab-2" className={`tab-pane fade show p-0 ${activeTab === 'tab-2' ? 'active' : ''}`}>
                  <ProductGrid categories={luminousCategories} currentPage={luminousPage} brand="luminous" searchQuery="" />
                  <Pagination totalPages={luminousCategories.length} currentPage={luminousPage} onPageChange={setLuminousPage} paginationId="luminous-pagination" />
                </div>

                <div id="tab-3" className={`tab-pane fade show p-0 ${activeTab === 'tab-3' ? 'active' : ''}`}>
                  <ProductGrid categories={amaronCategories} currentPage={amaronPage} brand="amaron" searchQuery="" />
                  <Pagination totalPages={amaronCategories.length} currentPage={amaronPage} onPageChange={setAmaronPage} paginationId="amaron-pagination" />
                </div>

                <div id="tab-5" className={`tab-pane fade show p-0 ${activeTab === 'tab-5' ? 'active' : ''}`}>
                  <ProductGrid categories={lithiumCategories} currentPage={lithiumPage} brand="lithium" searchQuery="" />
                  <Pagination totalPages={lithiumCategories.length} currentPage={lithiumPage} onPageChange={setLithiumPage} paginationId="lithium-pagination" />
                </div>

                <div id="tab-4" className={`tab-pane fade show p-0 ${activeTab === 'tab-4' ? 'active' : ''}`}>
                  <ProductGrid categories={solarCategories} currentPage={solarPage} brand="solar" searchQuery="" />
                  <Pagination totalPages={solarCategories.length} currentPage={solarPage} onPageChange={setSolarPage} paginationId="solar-pagination" />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;